/**
 * 計助 V1 ダッシュボード用: 表紙 + 結果 + グラフの一式 PDF（html2canvas + jsPDF）
 */

export interface KeisukeFullReportPdfOptions {
  /** キャプチャ対象（結果タブ周りのルート。内部のスクロール領域を展開して撮影） */
  resultsRoot: HTMLElement;
  chartRoot: HTMLElement;
  coverTitle: string;
  coverSubtitleLines?: string[];
  filename?: string;
}

function restoreStyles(restores: Array<() => void>): void {
  for (let i = restores.length - 1; i >= 0; i--) restores[i]();
}

/** スクロールで隠れている内容も含めてラスタ化しやすくする */
function temporarilyUnclipScrollRoots(root: HTMLElement): () => void {
  const restores: Array<() => void> = [];
  const candidates = [
    root,
    ...Array.from(root.querySelectorAll<HTMLElement>('[data-keisuke-report-tab-body]')),
  ];
  for (const el of candidates) {
    if (!el) continue;
    const overflow = el.style.overflow;
    const maxHeight = el.style.maxHeight;
    const height = el.style.height;
    el.style.overflow = 'visible';
    el.style.maxHeight = 'none';
    el.style.height = 'auto';
    restores.push(() => {
      el.style.overflow = overflow;
      el.style.maxHeight = maxHeight;
      el.style.height = height;
    });
  }
  return () => restoreStyles(restores);
}

/**
 * 画面上の論文プレビューは 15px 前後で密度優先。PDF ラスタは読みやすさ優先で一時的に上げる。
 */
function temporarilyBoostPaperBodyFontForPdf(root: HTMLElement): () => void {
  const prev = root.style.getPropertyValue('--keisuke-paper-body-font-size');
  root.style.setProperty('--keisuke-paper-body-font-size', '17px');
  return () => {
    if (prev) root.style.setProperty('--keisuke-paper-body-font-size', prev);
    else root.style.removeProperty('--keisuke-paper-body-font-size');
  };
}

async function captureToCanvas(el: HTMLElement, scale: number): Promise<HTMLCanvasElement> {
  const undoClip = temporarilyUnclipScrollRoots(el);
  const undoPaper = temporarilyBoostPaperBodyFontForPdf(el);
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  const html2canvas = (await import('html2canvas')).default;
  try {
    return await html2canvas(el, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: el.scrollWidth,
      windowHeight: el.scrollHeight,
    });
  } finally {
    undoPaper();
    undoClip();
  }
}

type JsPdf = import('jspdf').jsPDF;

/**
 * 画像を幅に合わせ、必要なら縦に分割して複数ページへ貼り付け
 * @param headerReserveMm 上端から画像開始までの距離（mm）。1ページ目の見出し分。
 */
function addCanvasRasterPaged(
  pdf: JsPdf,
  canvas: HTMLCanvasElement,
  marginMm: number,
  headerReserveMm: number
): void {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgWpx = canvas.width;
  const imgHpx = canvas.height;
  if (imgWpx <= 0 || imgHpx <= 0) return;

  const dispWMm = pageW - 2 * marginMm;
  const pxPerMmDisplay = imgWpx / dispWMm;
  const usableFirstMm = pageH - headerReserveMm - marginMm;
  const usableRestMm = pageH - 2 * marginMm;
  const imgData = canvas.toDataURL('image/png');

  let srcY = 0;
  let first = true;
  while (srcY < imgHpx - 0.5) {
    const usableMm = first ? usableFirstMm : usableRestMm;
    const maxSliceHpx = usableMm * pxPerMmDisplay;
    const sliceHpx = Math.min(maxSliceHpx, imgHpx - srcY);
    if (!first) pdf.addPage();
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = imgWpx;
    sliceCanvas.height = Math.ceil(sliceHpx);
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, srcY, imgWpx, sliceHpx, 0, 0, imgWpx, sliceHpx);
    const sliceData = sliceCanvas.toDataURL('image/png');
    const dispHMm = (sliceHpx / imgWpx) * dispWMm;
    const y0 = first ? headerReserveMm : marginMm;
    pdf.addImage(sliceData, 'PNG', marginMm, y0, dispWMm, dispHMm);
    srcY += sliceHpx;
    first = false;
  }
}

export async function downloadKeisukeDashboardFullReportPdf(opts: KeisukeFullReportPdfOptions): Promise<void> {
  const { default: jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const margin = 14;

  pdf.setFontSize(18);
  pdf.text(opts.coverTitle, margin, 28);
  pdf.setFontSize(10);
  let y = 36;
  for (const line of opts.coverSubtitleLines ?? []) {
    const parts = pdf.splitTextToSize(line, pdf.internal.pageSize.getWidth() - 2 * margin);
    for (const p of parts) {
      pdf.text(p, margin, y);
      y += 5;
    }
  }
  pdf.setFontSize(8);
  pdf.setTextColor(100, 100, 100);
  pdf.text(`Generated (UTC): ${new Date().toISOString().slice(0, 19)}`, margin, Math.min(y + 8, 285));
  pdf.setTextColor(0, 0, 0);

  const resultsCanvas = await captureToCanvas(opts.resultsRoot, 1.35);
  pdf.addPage();
  pdf.setFontSize(13);
  pdf.text('Results', margin, margin + 2);
  addCanvasRasterPaged(pdf, resultsCanvas, margin, margin + 10);

  const chartCanvas = await captureToCanvas(opts.chartRoot, 1.35);
  pdf.addPage();
  pdf.setFontSize(13);
  pdf.text('Chart', margin, margin + 2);
  addCanvasRasterPaged(pdf, chartCanvas, margin, margin + 10);

  const stamp = new Date().toISOString().slice(0, 10);
  pdf.save(opts.filename ?? `keisuke-report-${stamp}.pdf`);
}
